"use client";

import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Resseti",
  url: "https://resseti.com",
  description: "Setura is a diagnostic engine that maps student performance to mark-scheme logic, surfacing the precise process errors that cause marks to be lost.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Cambridge",
    addressCountry: "GB"
  },
  founder: {
    "@type": "Person",
    name: "Ayush Jha",
    url: "https://resseti.com/AyushJha"
  },
  sameAs: ["https://linkedin.com/company/resseti", "https://crunchbase.com/company/resseti"]
};
export default function Diagnostic() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: metaRef,
    isVisible: metaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(organizationSchema)
    }} />
      <section data-section-id="3626" id="the-diagnostic-standard" className="bg-background text-foreground relative min-h-screen w-full overflow-hidden">
        {/* Flat CSS graph-paper grid pattern */}
        <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: `
              linear-gradient(to right, rgba(255, 255, 255, 0.05) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255, 255, 255, 0.05) 1px, transparent 1px)
            `,
        backgroundSize: "32px 32px"
      }} />

        {/* Top border line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-border/60" />

        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-[1600px] flex-col justify-center px-6 py-24 sm:px-10 lg:px-16">
          <div className="grid flex-1 grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
            {/* Left Content Column */}
            <div ref={headlineRef} className={`lg:col-span-6 xl:col-span-6 flex flex-col justify-center transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <h1 className="font-default font-bold uppercase leading-[0.88] tracking-tight text-foreground break-normal">
                <span className="block text-[clamp(1.85rem,6.8vw,6.5rem)] xl:text-[clamp(2.5rem,7.5vw,8rem)]">
                  EVERY LOST MARK
                </span>
                <span className="block text-[clamp(1.85rem,6.8vw,6.5rem)] !p-0 xl:text-[clamp(2.5rem,7.5vw,8rem)]">
                  HAS A CAUSE.
                </span>
              </h1>

              <div className="my-10 h-[2px] w-20 bg-primary" />

              <p className="font-default text-lg sm:text-xl text-muted-foreground max-w-[540px] leading-relaxed">
                Setura reads a student&apos;s working line by line against the mark scheme, then tells the teacher whether the mark was lost to a gap in knowledge or a slip in method.
              </p>

              <div className="mt-12 flex flex-col sm:flex-row gap-4 sm:items-center">
                <Button asChild size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive has-[>svg]:px-4 group h-14 px-8 bg-primary text-primary-foreground font-default font-bold uppercase tracking-wider text-sm shadow-lg transition-all duration-300 rounded-lg hover:text-primary hover:-translate-y-0.5 hover:bg-secondary">
                  <Link to="/PilotPage">
                    Book a pilot call
                    <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button asChild variant="link" className="font-mono text-xs uppercase tracking-[0.25em] text-foreground hover:text-primary h-14 px-2">
                  <Link to="/SeturaPage">See how Setura works →</Link>
                </Button>
              </div>
            </div>

            {/* Right Placeholder Column */}
            <div ref={metaRef} className={`lg:col-span-6 xl:col-span-6 flex items-center justify-center transition-all duration-1000 delay-300 ease-out ${metaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <motion.div animate={{
              y: [0, -8, 0]
            }} transition={{
              duration: 4.5,
              repeat: Infinity,
              ease: "easeInOut"
            }} className="w-full flex justify-center">
                <div className="w-full max-w-2xl border border-border rounded-md overflow-hidden hover:-translate-y-1 hover:scale-[1.01] transition-transform duration-300">
                  <img data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/setura_diagnostic_dashboard_interface.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714093/img/setura_diagnostic_dashboard_interface.png-wvc-sizes" alt="Diagnostic dashboard interface displaying student working mapped to mark-scheme criteria" loading="lazy" className="w-full aspect-[4/3] max-w-2xl object-cover rounded-md" src="https://resseti.com/wp-content/uploads/2026/09/02-browser-diagnosis-1.webp" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Bottom border line */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border/60" />
      </section>
    </>;
}